import { MAX_ITERATIONS, SMOOTHING_STEPS } from './fractalIteration';
import { PALETTE_SIZE, paletteTable } from './fractalPalette';
import { COMPENSATED_ARITHMETIC_GLSL, createWebGLProgram, getWebGLContext,
  needsDeepZoom, splitFloat, prepareWebGLProgressiveFrame, copyWebGLProgressiveTile, commitWebGLProgressiveFrame, drawMandelbrotFractalWebGL } from './fractalWebGL';

const MAX_BATCH = 128;
const RECURRENCE_COST_WINDOW = 4;
const COST_GEOMETRY_LIMIT = 32;
const METADATA_BYTES = 32768;
const CALIBRATION_SIZE = 256;
const BACKGROUND = [10 / 255, 14 / 255, 24 / 255, 1];
const ZERO_FLOAT4 = new Float32Array(4);
const VERTEX = `#version 300 es
precision highp float;
in vec2 a_position;
out vec2 v_uv;
void main() { v_uv = (a_position + 1.0) * 0.5; gl_Position = vec4(a_position, 0.0, 1.0); }`;

// Both renderers use exactly the same compensated operations. Orbit state is
// written without color quantization, so a boundary between slices changes
// neither recurrence rounding nor the escaped-orbit smoothing convention.
export const PROGRESSIVE_ITERATION_SHADER = `#version 300 es
precision highp float;
precision highp int;
in vec2 v_uv;
uniform sampler2D u_orbit;
uniform sampler2D u_meta;
uniform vec2 u_xRange;
uniform vec2 u_yRange;
uniform vec2 u_centerX;
uniform vec2 u_centerY;
uniform vec2 u_spanX;
uniform vec2 u_spanY;
uniform vec2 u_resolution;
uniform vec2 u_origin;
uniform float u_arithmeticBound;
uniform int u_steps;
uniform bool u_init;
#ifdef COMPENSATED
const bool u_compensated = true;
#else
const bool u_compensated = false;
#endif
uniform bool u_calibrate;
layout(location = 0) out vec4 outOrbit;
layout(location = 1) out vec4 outMeta;
${COMPENSATED_ARITHMETIC_GLSL}
void main() {
  ivec2 pixel = ivec2(gl_FragCoord.xy);
  vec4 orbit = u_init ? vec4(0.0) : texelFetch(u_orbit, pixel, 0);
  vec4 meta = u_init ? vec4(0.0) : texelFetch(u_meta, pixel, 0);
  if (meta.z != 0.0) { outOrbit = orbit; outMeta = meta; return; }
  vec2 uv = (u_origin + gl_FragCoord.xy) / u_resolution;
  vec2 cx = dsAdd(u_centerX, dsMul(u_spanX, vec2(uv.x - 0.5, 0.0)));
  vec2 cy = dsAdd(u_centerY, dsMul(u_spanY, vec2(0.5 - uv.y, 0.0)));
  float x0 = u_compensated ? cx.x + cx.y : mix(u_xRange.x, u_xRange.y, v_uv.x);
  float y0 = u_compensated ? cy.x + cy.y : mix(u_yRange.y, u_yRange.x, v_uv.y);
  if (u_init && !u_compensated && !u_calibrate) {
    float q = (x0 - 0.25) * (x0 - 0.25) + y0 * y0;
    if (q * (q + x0 - 0.25) <= 0.25 * y0 * y0 ||
      (x0 + 1.0) * (x0 + 1.0) + y0 * y0 <= 0.0625) {
      outOrbit = orbit; outMeta = vec4(${MAX_ITERATIONS}.0, 0.0, 2.0, 0.0); return;
    }
  }
  vec2 zx = orbit.xy;
  vec2 zy = orbit.zw;
  float x = zx.x, y = zy.x;
  float x2 = x * x, y2 = y * y;
  vec2 zx2 = u_compensated ? dsMul(zx, zx) : vec2(0.0);
  vec2 zy2 = u_compensated ? dsMul(zy, zy) : vec2(0.0);
  bool escaped = false;
  for (int i = 0; i < ${MAX_BATCH}; i++) {
    if (i >= u_steps || meta.x >= ${MAX_ITERATIONS}.0) break;
    if (u_compensated) {
      vec2 nextX = dsAdd(dsAdd(zx2, -zy2), cx);
      zy = dsAdd(2.0 * dsMul(zx, zy), cy); zx = nextX;
      zx2 = dsMul(zx, zx); zy2 = dsMul(zy, zy);
      vec2 radius = dsAdd(zx2, zy2);
      escaped = radius.x > 4.0 || (radius.x == 4.0 && radius.y > 0.0);
    } else {
      float nextX = x2 - y2 + x0;
      y = 2.0 * x * y + y0; x = nextX; x2 = x * x; y2 = y * y;
      escaped = x2 + y2 > 4.0;
    }
    meta.x += 1.0;
    if (escaped) break;
  }
  if (escaped) {
    if (u_compensated) { x = zx.x + zx.y; y = zy.x + zy.y; x2 = x * x; y2 = y * y; }
    for (int i = 0; i < ${SMOOTHING_STEPS}; i++) {
      float nextX = x2 - y2 + x0;
      y = 2.0 * x * y + y0; x = nextX; x2 = x * x; y2 = y * y;
    }
    float logDist = 0.5 * log(x2 + y2);
    meta.y = meta.x + ${SMOOTHING_STEPS + 1}.0 - log(logDist) / log(2.0);
    meta.z = 1.0;
  } else if (meta.x >= ${MAX_ITERATIONS}.0) meta.z = 2.0;
  outOrbit = u_compensated ? vec4(zx, zy) : vec4(x, 0.0, y, 0.0);
  outMeta = meta;
}`;

const REDUCTION = `#version 300 es
precision highp float;
uniform sampler2D u_meta;
out vec4 outColor;
void main() { if (texelFetch(u_meta, ivec2(gl_FragCoord.xy), 0).z != 0.0) discard; outColor = vec4(0.0); }`;
const COLOR = `#version 300 es
precision highp float;
uniform sampler2D u_meta;
uniform sampler2D u_palette;
uniform ivec2 u_origin;
uniform float u_gScale, u_phaseOffset, u_aScale, u_aFixed;
uniform bool u_lagoon, u_repeat, u_useMu, u_useFixedAlpha;
out vec4 outColor;
void main() {
  vec4 meta = texelFetch(u_meta, ivec2(gl_FragCoord.xy) - u_origin, 0);
  vec3 background = vec3(${BACKGROUND.slice(0, 3).join(', ')});
  if (meta.z != 1.0) { outColor = vec4(background, 1.0); return; }
  float val = u_useMu ? meta.y : meta.x;
  float position = val * u_gScale / 255.0;
  float t = u_repeat ? 0.5 - 0.5 * cos(3.141592653589793 * position) : clamp(position, 0.0, 1.0);
  float alpha = (u_useFixedAlpha ? u_aFixed : clamp(val * u_aScale, 0.0, 255.0)) / 255.0;
  vec3 color;
  if (u_lagoon) {
    float phase = (sqrt(max(0.0, val) + 1.0) - 1.0) * u_gScale / 55.0 + u_phaseOffset;
    color = texture(u_palette, vec2(fract(phase) + 0.5 / ${PALETTE_SIZE}.0, 0.5)).rgb;
  } else color = mix(vec3(0.0, 0.0, 1.0), vec3(1.0, 1.0, 0.0), t);
  outColor = vec4(mix(background, color, alpha), 1.0);
}`;
const COMPOSE_VERTEX = `#version 300 es
precision highp float;
in vec2 a_position;
in vec4 a_bounds;
in vec4 a_uvBounds;
out vec2 v_uv;
flat out vec4 v_clamp;
uniform vec2 u_textureSize;
void main() {
  vec2 uv = (a_position + 1.0) * 0.5;
  gl_Position = vec4(mix(a_bounds.xy, a_bounds.zw, uv) * 2.0 - 1.0, 0.0, 1.0);
  v_uv = mix(a_uvBounds.xy, a_uvBounds.zw, uv);
  v_clamp = vec4(a_uvBounds.xy + 0.5 / u_textureSize, a_uvBounds.zw - 0.5 / u_textureSize);
}`;
const COMPOSE = `#version 300 es
precision highp float;
uniform sampler2D u_image;
in vec2 v_uv;
flat in vec4 v_clamp;
out vec4 outColor;
void main() { outColor = texture(u_image, clamp(v_uv, v_clamp.xy, v_clamp.zw)); }`;
const QUAD = new Float32Array([-1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1]);
const now = () => typeof performance !== 'undefined' ? performance.now() : Date.now();
const sameCamera = (a, b) => !!(a && b && a.key === b.key);
const matchesFrame = (source, requested) => !!(source && requested && source.exact !== false &&
  source.width === requested.width && source.height === requested.height &&
  source.paletteKey === requested.paletteKey && source.deepZoom === requested.deepZoom &&
  source.xRange[0] === requested.xRange[0] && source.xRange[1] === requested.xRange[1] &&
  source.yRange[0] === requested.yRange[0] && source.yRange[1] === requested.yRange[1]);
const identity = (xr, yr, w, h, paletteKey, deepZoom) =>
  `${xr[0]},${xr[1]},${yr[0]},${yr[1]},${w},${h},${paletteKey},${deepZoom}`;

export function progressiveTileBounds(frame, tile, xRange, yRange) {
  const dx = frame.xRange[1] - frame.xRange[0], dy = frame.yRange[1] - frame.yRange[0];
  const sx = xRange[1] - xRange[0], sy = yRange[1] - yRange[0];
  const left = frame.xRange[0] + tile.x / frame.width * dx;
  const bottom = frame.yRange[1] - tile.y / frame.height * dy;
  return [(left - xRange[0]) / sx, (yRange[1] - bottom) / sy,
    (left + tile.width / frame.width * dx - xRange[0]) / sx,
    (yRange[1] - bottom + tile.height / frame.height * dy) / sy];
}
const visible = bounds => bounds[0] < 1 && bounds[1] < 1 && bounds[2] > 0 && bounds[3] > 0;

/** Pure memory admission, also used by deterministic tests. Two RGBA32F
 * attachments, two ping-pong copies, and two continuations cost 128 B/pixel. */
export function progressiveStoragePlan(width, height, tileSize = 64, maxBytes = 32 * 1024 * 1024, maxTextureSize = 4096) {
  const columns = Math.ceil(width / tileSize), rows = Math.ceil(height / tileSize);
  const minimumTiles = columns * rows;
  const stateBytes = tileSize * tileSize * 128 + CALIBRATION_SIZE * CALIBRATION_SIZE * 32;
  const perSlot = tileSize * tileSize * 4 + 160;
  let capacity = Math.min(minimumTiles + Math.max(4, Math.floor(262144 / (tileSize * tileSize))),
    Math.floor((maxBytes - stateBytes - METADATA_BYTES) / perSlot));
  if (capacity < minimumTiles + 1 || tileSize > maxTextureSize) return null;
  while (capacity >= minimumTiles + 1) {
    const atlasColumns = Math.min(Math.floor(maxTextureSize / tileSize), Math.ceil(Math.sqrt(capacity)));
    const atlasRows = Math.ceil(capacity / atlasColumns);
    const atlasBytes = atlasColumns * atlasRows * tileSize * tileSize * 4;
    const bytes = atlasBytes + stateBytes + capacity * 160 + METADATA_BYTES;
    if (atlasRows * tileSize <= maxTextureSize && bytes <= maxBytes) return {
      tileSize, columns, rows, capacity, atlasColumns, atlasRows, bytes,
      atlasWidth: atlasColumns * tileSize, atlasHeight: atlasRows * tileSize };
    capacity--;
  }
  return null;
}

/** Returns null when a bounded, asynchronous Number path is unavailable. */
export function createProgressiveWebGLRenderer(canvas, options = {}) {
  const context = getWebGLContext(canvas);
  if (!context?.isWebGL2) return null;
  const gl = context.gl;
  if (gl.isContextLost?.() || !gl.getExtension('EXT_color_buffer_float') ||
      !gl.fenceSync || !gl.clientWaitSync || !gl.drawArraysInstanced || !gl.createQuery) return null;
  if ((gl.getShaderPrecisionFormat(gl.FRAGMENT_SHADER, gl.HIGH_FLOAT)?.precision ?? 0) < 23) return null;
  const width = canvas.width, height = canvas.height;
  if (!(width > 0 && height > 0) || width * height > 2880000) return null;
  let plan = progressiveStoragePlan(width, height, options.tileSize, options.maxBytes,
    gl.getParameter(gl.MAX_TEXTURE_SIZE));
  if (!plan) return null;
  const retained = options.retainedAccepted;
  if (retained) {
    if (retained.taken || retained.released || retained.gl !== gl || retained.width !== width || retained.height !== height ||
        !retained.atlas || !retained.composeProgram || !retained.quadBuffer || !retained.instancesBuffer || !Array.isArray(retained.entries) ||
        retained.tileSize <= plan.tileSize || retained.atlasWidth % plan.tileSize || retained.atlasHeight % plan.tileSize) return null;
    const atlasColumns = retained.atlasWidth / plan.tileSize, atlasRows = retained.atlasHeight / plan.tileSize;
    const capacity = atlasColumns * atlasRows;
    const bytes = retained.atlasWidth * retained.atlasHeight * 4 + plan.tileSize * plan.tileSize * 128 +
      CALIBRATION_SIZE * CALIBRATION_SIZE * 32 + capacity * 160 + METADATA_BYTES;
    if (capacity < plan.columns * plan.rows + 1 || bytes > (options.maxBytes ?? 32 * 1024 * 1024)) return null;
    plan = { ...plan, atlasColumns, atlasRows, capacity, bytes,
      atlasWidth: retained.atlasWidth, atlasHeight: retained.atlasHeight };
  }
  const resources = { textures: [], framebuffers: [], programs: [], buffers: [], queries: [] };
  const parallel = gl.getExtension('KHR_parallel_shader_compile');
  const timerExtension = gl.getExtension('EXT_disjoint_timer_query_webgl2');
  let disposed = false, destroyed = false, failed = false, inFlight = null;
  let latest = null, priorIdentity = null, interactive = false, sequence = 0;
  const initialBatchSteps = Math.min(MAX_BATCH, options.initialBatchSteps || 32);
  let batchSteps = initialBatchSteps, stableSlices = 0;
  let nextKind = 'latest', generation = 0, lastExactKey = null, assembly = null;
  let disposePromise = null, resolveDisposal = null, retirementFence = null;
  let retainAcceptedOnDisposal = false;
  let compositionTimer = null, compositionSequence = 0;
  let minimalOverruns = 0;
  let calibration = null, directCompleted = null, directAssembly = null;
  const recurrenceCosts = [new Map(), new Map()], startupCosts = [new Map(), new Map()];
  const colorCosts = [new Map(), new Map()], copyCosts = [new Map(), new Map()];
  const rejectedDirectFixtures = new Set();
  const cache = new Map(), continuations = [null, null], freeSlots = [];
  for (let i = plan.capacity - 1; i >= 0; i--) freeSlots.push(i);
  const stats = { admittedBytes: plan.bytes, submitted: 0, acceptedTiles: 0, discardedTiles: 0,
    overshoots: 0, latestSubmissions: 0, progressSubmissions: 0, batchSteps, gpuMs: null,
    timingSource: null, cameraAgeMs: 0, coverage: 0, ready: false, nextKind: 'latest',
    compositionGpuMs: null, compositionEstimateMs: 0.35, compositionRecentEstimateMs: 0.35,
    compositionTimingSource: 'estimate', compositionTimingVersion: 0 };

  const texture = (w, h, float = false) => {
    const result = gl.createTexture(); if (!result) throw new Error('Texture allocation failed');
    resources.textures.push(result); gl.bindTexture(gl.TEXTURE_2D, result);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, float ? gl.NEAREST : gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, float ? gl.NEAREST : gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texImage2D(gl.TEXTURE_2D, 0, float ? gl.RGBA32F : gl.RGBA8, w, h, 0, gl.RGBA,
      float ? gl.FLOAT : gl.UNSIGNED_BYTE, null); return result;
  };
  const framebuffer = (textures, clear = true) => {
    const result = gl.createFramebuffer(); if (!result) throw new Error('Framebuffer allocation failed');
    resources.framebuffers.push(result); gl.bindFramebuffer(gl.FRAMEBUFFER, result);
    textures.forEach((value, index) => gl.framebufferTexture2D(gl.FRAMEBUFFER,
      gl.COLOR_ATTACHMENT0 + index, gl.TEXTURE_2D, value, 0));
    gl.drawBuffers(textures.map((_, index) => gl.COLOR_ATTACHMENT0 + index));
    if (gl.checkFramebufferStatus(gl.FRAMEBUFFER) !== gl.FRAMEBUFFER_COMPLETE) throw new Error('Orbit state is not renderable');
    if (clear) {
      gl.clearBufferfv(gl.COLOR, 0, ZERO_FLOAT4);
      if (textures.length > 1) gl.clearBufferfv(gl.COLOR, 1, ZERO_FLOAT4);
    }
    return result;
  };
  const program = (vertex, fragment, names) => {
    const result = createWebGLProgram(gl, vertex, fragment, !!parallel); resources.programs.push(result);
    const value = { program: result, attributes: {}, uniforms: {} };
    for (const name of names) value.uniforms[name] = null;
    return value;
  };
  let iterationPrograms, colorProgram, reductionProgram, composeProgram, atlas, atlasFramebuffer, directFramebuffer;
  let palette, quadBuffer, instancesBuffer, statePool, calibrationState;
  if (retained) {
    atlas = retained.atlas; composeProgram = retained.composeProgram;
    quadBuffer = retained.quadBuffer; instancesBuffer = retained.instancesBuffer;
    resources.textures.push(atlas); resources.programs.push(composeProgram.program);
    resources.buffers.push(quadBuffer, instancesBuffer); retained.claim();
  }
  const destroy = () => {
    if (destroyed) return; destroyed = true;
    const preserve = retainAcceptedOnDisposal && !gl.isContextLost?.() && (stats.ready || retained) && atlas;
    const handoff = preserve ? { gl, width, height, tileSize: plan.tileSize, atlas,
      atlasWidth: plan.atlasWidth, atlasHeight: plan.atlasHeight, composeProgram, quadBuffer, instancesBuffer,
      entries: [...cache.values()].map(value => ({ ...value, frame: { ...value.frame,
        xRange: [...value.frame.xRange], yRange: [...value.frame.yRange] }, tile: { ...value.tile },
        atlasX: value.slot % plan.atlasColumns * plan.tileSize,
        atlasY: Math.floor(value.slot / plan.atlasColumns) * plan.tileSize })),
      taken: false, released: false,
      release() {
        if (this.taken || this.released) return;
        this.released = true;
        gl.deleteTexture(this.atlas); gl.deleteProgram(this.composeProgram.program);
        gl.deleteBuffer(this.quadBuffer); gl.deleteBuffer(this.instancesBuffer);
        stats.admittedBytes = 0;
      },
      claim() { this.taken = true; stats.admittedBytes = 0; },
    } : null;
    for (const value of resources.textures) if (!preserve || value !== atlas) gl.deleteTexture(value);
    for (const value of resources.framebuffers) gl.deleteFramebuffer(value);
    for (const value of resources.programs) if (!preserve || value !== composeProgram.program) gl.deleteProgram(value);
    for (const value of resources.buffers) if (!preserve || value !== quadBuffer && value !== instancesBuffer) gl.deleteBuffer(value);
    for (const value of resources.queries) gl.deleteQuery(value);
    cache.clear(); continuations.fill(null);
    for (const costs of [startupCosts, recurrenceCosts, colorCosts, copyCosts]) for (const values of costs) values.clear();
    if (!preserve) canvas._fractalProgressiveTiles = [];
    stats.admittedBytes = preserve ? plan.atlasWidth * plan.atlasHeight * 4 + handoff.entries.length * 160 + 4096 : 0;
    resolveDisposal?.(handoff);
  };
  try {
    const iterationUniforms = ['u_orbit', 'u_meta', 'u_xRange', 'u_yRange', 'u_centerX', 'u_centerY',
      'u_spanX', 'u_spanY', 'u_resolution', 'u_origin', 'u_arithmeticBound', 'u_steps', 'u_init', 'u_calibrate'];
    iterationPrograms = [program(VERTEX, PROGRESSIVE_ITERATION_SHADER, iterationUniforms),
      program(VERTEX, PROGRESSIVE_ITERATION_SHADER.replace('#version 300 es', '#version 300 es\n#define COMPENSATED'), iterationUniforms)];
    reductionProgram = program(VERTEX, REDUCTION, ['u_meta']);
    colorProgram = program(VERTEX, COLOR, ['u_meta', 'u_palette', 'u_origin', 'u_gScale', 'u_phaseOffset',
      'u_aScale', 'u_aFixed', 'u_lagoon', 'u_repeat', 'u_useMu', 'u_useFixedAlpha']);
    composeProgram = retained?.composeProgram || program(COMPOSE_VERTEX, COMPOSE, ['u_image', 'u_textureSize']);
    atlas = retained?.atlas || texture(plan.atlasWidth, plan.atlasHeight);
    atlasFramebuffer = framebuffer([atlas], !retained);
    palette = texture(PALETTE_SIZE, 1); gl.bindTexture(gl.TEXTURE_2D, palette);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA8, PALETTE_SIZE, 1, 0, gl.RGBA, gl.UNSIGNED_BYTE, paletteTable);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.REPEAT);
    quadBuffer = retained?.quadBuffer || gl.createBuffer(); instancesBuffer = retained?.instancesBuffer || gl.createBuffer();
    if (!quadBuffer || !instancesBuffer) throw new Error('Geometry allocation failed');
    if (!retained) resources.buffers.push(quadBuffer, instancesBuffer);
    if (!retained) { gl.bindBuffer(gl.ARRAY_BUFFER, quadBuffer); gl.bufferData(gl.ARRAY_BUFFER, QUAD, gl.STATIC_DRAW); }
    if (plan.atlasWidth >= CALIBRATION_SIZE || options.enableDirect !== false) {
      const orbit = texture(CALIBRATION_SIZE, CALIBRATION_SIZE, true), meta = texture(CALIBRATION_SIZE, CALIBRATION_SIZE, true);
      calibrationState = { orbit, meta, framebuffer: framebuffer([orbit, meta]) };
    }
    statePool = [0, 1].map(() => [0, 1].map(() => {
      const orbit = texture(plan.tileSize, plan.tileSize, true), meta = texture(plan.tileSize, plan.tileSize, true);
      return { orbit, meta, framebuffer: framebuffer([orbit, meta]) };
    }));
  } catch (_error) { destroy(); gl.bindFramebuffer(gl.FRAMEBUFFER, null); return null; }
  if (retained) {
    const occupied = new Set();
    for (const entry of retained.entries) {
      const slots = [];
      for (let y = entry.atlasY; y < entry.atlasY + entry.tile.height; y += plan.tileSize)
        for (let x = entry.atlasX; x < entry.atlasX + entry.tile.width; x += plan.tileSize) {
          const slot = y / plan.tileSize * plan.atlasColumns + x / plan.tileSize;
          occupied.add(slot); slots.push(slot);
        }
      const value = { ...entry, id: `${entry.id}:retained:${retained.tileSize}`, slot: slots[0], slots,
        retained: true, generation };
      cache.set(value.id, value); sequence = Math.max(sequence, value.sequence);
    }
    freeSlots.length = 0;
    for (let slot = plan.capacity - 1; slot >= 0; slot--) if (!occupied.has(slot)) freeSlots.push(slot);
    stats.retainedAcceptedTiles = cache.size;
  }
  gl.bindFramebuffer(gl.FRAMEBUFFER, null);

  const ready = () => {
    if (stats.ready) return true;
    for (const value of [...iterationPrograms, colorProgram, reductionProgram, composeProgram]) {
      if (parallel && !gl.getProgramParameter(value.program, parallel.COMPLETION_STATUS_KHR)) return false;
      if (!gl.getProgramParameter(value.program, gl.LINK_STATUS)) { failed = true; return false; }
    }
    for (const value of [...iterationPrograms, colorProgram, reductionProgram, composeProgram]) {
      value.attributes.a_position = gl.getAttribLocation(value.program, 'a_position');
      for (const name of Object.keys(value.uniforms)) value.uniforms[name] = gl.getUniformLocation(value.program, name);
    }
    composeProgram.attributes.a_bounds = gl.getAttribLocation(composeProgram.program, 'a_bounds');
    composeProgram.attributes.a_uvBounds = gl.getAttribLocation(composeProgram.program, 'a_uvBounds');
    stats.ready = true; return true;
  };
  const bindQuad = value => {
    gl.useProgram(value.program); gl.bindBuffer(gl.ARRAY_BUFFER, quadBuffer);
    gl.enableVertexAttribArray(value.attributes.a_position);
    gl.vertexAttribPointer(value.attributes.a_position, 2, gl.FLOAT, false, 0, 0);
    gl.vertexAttribDivisor(value.attributes.a_position, 0);
  };
  const bindTexture = (value, unit, location) => {
    gl.activeTexture(gl.TEXTURE0 + unit); gl.bindTexture(gl.TEXTURE_2D, value); gl.uniform1i(location, unit);
  };
  const removeTile = value => { cache.delete(value.id); freeSlots.push(...(value.slots || [value.slot])); };
  const sampleSpacing = frame => Math.max((frame.xRange[1] - frame.xRange[0]) / width,
    (frame.yRange[1] - frame.yRange[0]) / height);
  const useful = continuation => continuation && !continuation.abandoned && latest &&
    continuation.frame.paletteKey === latest.paletteKey && continuation.frame.deepZoom === latest.deepZoom &&
    visible(progressiveTileBounds(continuation.frame, continuation.tile, latest.xRange, latest.yRange));
  const tileID = (frame, tile) => `${frame.key}:${tile.x}:${tile.y}${tile.canonical === false ? ':anchor' : ''}`;
  const recordContinuations = () => {
    stats.continuationStates = continuations.filter(Boolean).map(value => ({
      cameraRequestedAt: value.frame.cameraRequestedAt, canonical: value.tile.canonical !== false,
      count: value.count, phase: value.phase, latest: sameCamera(value.frame, latest), useful: !!useful(value),
      calibrated: value.startupRate != null || !!value.recurrenceRates?.length,
      resumedCalibrated: !!value.recurrenceRates?.length,
    }));
  };
  const pickTile = frame => {
    const anchor = frame.anchor;
    if (interactive && sameCamera(frame, latest)) {
      // Center first useful work on the pointer disk. A fixed grid boundary
      // through that disk otherwise leaves a stale strip during every camera
      // change, even though the nearest canonical tile makes steady progress.
      const alignment = Math.min(64, Math.max(1, plan.tileSize / 4));
      const origin = (center, dimension) => Math.max(0, Math.min(Math.floor((dimension - 1) / alignment) * alignment,
        Math.round((center - Math.min(plan.tileSize, dimension) / 2) / alignment) * alignment));
      const x = origin(anchor[0], width), y = origin(anchor[1], height);
      // Use the same aligned full-viewport interpolation origins as canonical
      // tiles. Keep up to a quarter-tile placement margin around the pointer;
      // clipping happens at the actual frame edge, never across its disk.
      const anchorTile = { x, y, width: Math.min(plan.tileSize, width - x),
        height: Math.min(plan.tileSize, height - y), canonical: false };
      const id = tileID(frame, anchorTile);
      if (!cache.has(id) && !continuations.some(value => value && !value.abandoned && value.id === id)) return anchorTile;
    }
    let chosen = null, distance = Infinity;
    for (let row = 0; row < plan.rows; row++) for (let column = 0; column < plan.columns; column++) {
      const tile = { canonical: true, x: column * plan.tileSize, y: row * plan.tileSize,
        width: Math.min(plan.tileSize, width - column * plan.tileSize),
        height: Math.min(plan.tileSize, height - row * plan.tileSize) };
      const id = tileID(frame, tile);
      if (cache.has(id) || continuations.some(value => value && !value.abandoned && value.id === id)) continue;
      const score = (tile.x + tile.width / 2 - anchor[0]) ** 2 + (tile.y + tile.height / 2 - anchor[1]) ** 2;
      if (score < distance) { chosen = tile; distance = score; }
    }
    return chosen;
  };
  const admit = (index, frame) => {
    const tile = pickTile(frame); if (!tile) return null;
    const value = { frame, tile, id: tileID(frame, tile), index, ping: 0, count: 0, initialized: false,
      phase: 'iterate', generation, abandoned: false, lastSubmitted: -Infinity, startupRate: null, recurrenceRates: [] };
    continuations[index] = value; return value;
  };
  const choose = preferredKind => {
    for (let i = 0; i < 2; i++) if (continuations[i] && (!useful(continuations[i]) || continuations[i].abandoned)) continuations[i] = null;
    let current = continuations.find(value => sameCamera(value?.frame, latest));
    if (!current) {
      let index = continuations.findIndex(value => !value);
      if (index < 0) {
        // Keep the earlier continuation that has most useful progress. The
        // other slot receives the newest camera at this retired boundary.
        index = continuations[0].count > continuations[1].count ? 1 : 0;
        stats.discardedTiles++; continuations[index] = null;
      }
      current = admit(index, latest);
    }
    const progress = continuations.find(value => value && value !== current);
    let selected = (preferredKind || nextKind) === 'progress' && useful(progress) ? progress : current || progress;
    if (!selected) return null;
    const kind = sameCamera(selected.frame, latest) ? 'latest' : 'progress';
    // Finish the current native pointer region between explicit reservations.
    // Alternating every packet can give a cheap anchor too little physical
    // retirement time before the next camera replaces it. The shared ledger's
    // explicit progress deadline still overrides this preference above.
    const currentAnchor = interactive && current?.tile.canonical === false;
    nextKind = kind === 'latest' && progress && !currentAnchor ? 'progress' : 'latest';
    stats.nextKind = nextKind;
    recordContinuations();
    return { value: selected, kind };
  };
  const request = (xRange, yRange, colorParams = {}, requestOptions = {}) => {
    if (disposed || failed || canvas.width !== width || canvas.height !== height) return false;
    const deepZoom = needsDeepZoom(xRange, yRange, width, height, latest?.deepZoom ?? canvas._fractalDeepZoom,
      canvas._fractalFloatPrecision ?? 23);
    const paletteKey = JSON.stringify(colorParams || {});
    const key = identity(xRange, yRange, width, height, paletteKey, deepZoom);
    interactive = !!requestOptions.interactive;
    if (latest?.key === key) return true;
    const hardIdentity = `${paletteKey}:${deepZoom}`;
    if (priorIdentity !== null && priorIdentity !== hardIdentity) {
      const paletteChanged = latest.paletteKey !== paletteKey;
      calibration = null; directCompleted = null; directAssembly = null;
      if (latest?.deepZoom !== deepZoom) {
        batchSteps = initialBatchSteps; stableSlices = 0;
        for (const costs of [recurrenceCosts, startupCosts, colorCosts, copyCosts]) costs[deepZoom ? 1 : 0].clear();
      }
      if (paletteChanged) for (const costs of [colorCosts, copyCosts]) for (const values of costs) values.clear();
      generation++; for (const value of continuations) if (value) value.abandoned = true;
      // Finished RGBA8 pixels retain their original camera and arithmetic.
      // They remain valid projected coverage while the new precision starts
      // bounded work. Orbit state and exact assembly cannot cross that change;
      // an incompatible palette still invalidates the finished pixels too.
      if (paletteChanged) for (const value of [...cache.values()]) removeTile(value);
    }
    priorIdentity = hardIdentity;
    const anchor = requestOptions.anchor || [width / 2, height / 2];
    latest = { key, xRange: [...xRange], yRange: [...yRange], width, height, paletteKey, deepZoom,
      colorParams: { ...colorParams }, cameraRequestedAt: requestOptions.cameraRequestedAt ?? now(),
      anchor: [anchor[0] ?? anchor.x ?? width / 2, height - (anchor[1] ?? anchor.y ?? height / 2)] };
    lastExactKey = null; assembly = null; canvas._fractalDeepZoom = deepZoom;
    stats.nextKind = continuations.some(value => useful(value) && !sameCamera(value.frame, latest)) ? nextKind : 'latest';
    recordContinuations();
    return true;
  };

  const geometryKey = value => `${value.tile.width}:${value.tile.height}`;
  // GPU launch/query costs do not scale with pixel area. A clipped rim's
  // observation can guard that same geometry, but cannot calibrate a larger
  // native unit by multiplying its fixed overhead per pixel. Unknown geometry
  // keeps the bounded startup/probe protocol and learns its own valid cost.
  const costsFor = (costs, value) => costs[value.frame.deepZoom ? 1 : 0];
  const geometryCost = (costs, value) => costsFor(costs, value).get(geometryKey(value));
  const rememberCost = (costs, value, cost) => {
    const values = costsFor(costs, value), key = geometryKey(value);
    if (!values.has(key) && values.size >= COST_GEOMETRY_LIMIT) values.delete(values.keys().next().value);
    values.set(key, cost);
  };
  const phaseEstimate = (phase, value) => {
    const cost = value ? geometryCost(phase === 'copy' ? copyCosts : colorCosts, value) : undefined;
    return Math.max(0.1, cost === undefined ? 0.35 : cost * 2.5 + 0.08);
  };
  const iterationPlan = (value, budgetMs) => {
    const resumedCalibrated = !!value.recurrenceRates?.length;
    const calibrated = value.startupRate != null || resumedCalibrated;
    const tileRates = value.recurrenceRates || [];
    const ownedRates = steps => tileRates.filter(sample => sample.quantum === steps).map(sample => sample.rate);
    const history = geometryCost(value.initialized ? recurrenceCosts : startupCosts, value) || [];
    const probeRate = calibrated ? Math.max(value.startupRate || 0, ...tileRates.map(sample => sample.rate)) : null;
    const rateFor = steps => {
      if (!value.initialized) return history.length ? Math.max(...history, value.startupRate || 0) : value.startupRate ?? null;
      const owned = ownedRates(steps);
      // A short resumed draw still includes fixed launch/query costs. It may
      // conservatively guard one probe, but cannot calibrate every larger
      // kernel. Only an owned valid observation of this quantum permits
      // grouping, guarded by shared observations of the same geometry,
      // arithmetic and quantum. Other tiles cannot qualify this probe.
      if (!owned.length) return probeRate;
      return Math.max(...owned, ...history.filter(sample => sample.quantum === steps).map(sample => sample.rate));
    };
    let stepsPerKernel = calibrated ? batchSteps : Math.min(batchSteps, initialBatchSteps);
    const kernelEstimateFor = steps => {
      const rate = rateFor(steps);
      return rate === null ? null : rate * steps * 2.5 + 0.02;
    };
    // The one-kernel floor must not admit a known oversized recurrence packet.
    // Reduce its iteration count first, retaining the configured cheap batch
    // size so valid later measurements can restore throughput without a reset.
    while (stepsPerKernel > 1 && kernelEstimateFor(stepsPerKernel) !== null &&
        kernelEstimateFor(stepsPerKernel) + 0.15 > budgetMs * 0.75)
      stepsPerKernel = Math.max(1, Math.floor(stepsPerKernel / 2));
    const remaining = Math.max(1, Math.ceil((MAX_ITERATIONS - value.count) / stepsPerKernel));
    const kernelEstimate = kernelEstimateFor(stepsPerKernel);
    let kernels = kernelEstimate === null ? 1 : Math.max(1, Math.min(8,
      Math.floor((budgetMs * 0.75 - 0.15) / kernelEstimate), remaining));
    // Initialization and the reduction query have fixed costs that a short
    // startup sample cannot attribute to every later iteration. Keep startup
    // separate from resumed observations, and admit only one conservative
    // resumed probe until this immutable tile has measured that phase too.
    // A different tile's cheap escaped state cannot establish either phase.
    const quantumCalibrated = ownedRates(stepsPerKernel).length > 0;
    if (!quantumCalibrated) kernels = 1;
    const finishEstimate = kernelEstimate === null ? Infinity : remaining * kernelEstimate + 0.15 + phaseEstimate('color', value);
    // A full final recurrence resolves every pixel, so color may follow in the
    // same bounded submission. Early query completion still requires a later
    // color submission; unfinished pixels are never installed.
    const finish = quantumCalibrated && value.phase === 'iterate' && geometryCost(colorCosts, value) !== undefined &&
      remaining <= 8 && finishEstimate <= budgetMs * 0.75;
    if (finish) kernels = remaining;
    return { kernels, finish, stepsPerKernel, estimateMs: kernelEstimate === null ? 0.5 :
      kernels * kernelEstimate + 0.15 + (finish ? phaseEstimate('color', value) : 0) };
  };
  const nextSlicePlan = ({ kind, budgetMs = 4 } = {}) => {
    if (!latest || disposed || failed) return { kind: 'latest', phase: null, estimateMs: 0 };
    const exactCache = canvas._fractalZoomCache;
    if (matchesFrame(exactCache?.frame, latest) || matchesFrame(exactCache?.previousFrame, latest))
      return { kind: 'latest', phase: 'replay', estimateMs: 0 };
    const progressDue = kind === 'progress' && continuations.some(value => useful(value) && !sameCamera(value.frame, latest));
    const exactTiles = [...cache.values()].filter(value => !value.retained && sameCamera(value.frame, latest) && value.tile.canonical !== false);
    if (!progressDue && exactTiles.length === plan.columns * plan.rows && lastExactKey !== latest.key &&
        (!assembly || exactTiles.some(value => !assembly.copied.has(value.id))))
      return { kind: 'latest', phase: 'copy', estimateMs: phaseEstimate('copy',
        exactTiles.find(value => !assembly?.copied.has(value.id))) };
    const qualified = options.directAdmission?.(latest, budgetMs);
    if (!progressDue && qualified?.fixtureKey && !rejectedDirectFixtures.has(qualified.fixtureKey) && Number.isFinite(qualified.estimateMs) &&
        qualified.estimateMs > 0 && qualified.estimateMs <= budgetMs)
      return { kind: 'latest', phase: 'direct', estimateMs: qualified.estimateMs };
    const directEnabled = options.enableDirect === true || (options.enableDirect !== false && !latest?.deepZoom);
    if (directEnabled && timerExtension && (!calibration || calibration.index < 8) && !continuations.some(Boolean))
      return { kind: 'latest', phase: 'calibrate', estimateMs: 0.5 };
    if (!progressDue && calibration?.fullGridEstimateMs != null && !calibration.directDisabled && calibration.fullGridEstimateMs <= budgetMs)
      return { kind: 'latest', phase: 'direct', estimateMs: calibration.fullGridEstimateMs };
    const retained = continuations.filter(useful);
    let current = retained.find(value => sameCamera(value.frame, latest));
    let progress = retained.find(value => value !== current);
    if (!current) {
      if (retained.length === 2) progress = retained[0].count > retained[1].count ? retained[0] : retained[1];
      const tile = pickTile(latest);
      if (tile) current = { frame: latest, tile, phase: 'iterate', count: 0 };
    }
    const selected = (kind || nextKind) === 'progress' && progress ? progress : current || progress;
    if (!selected) return { kind: 'latest', phase: null, estimateMs: 0 };
    const actualKind = sameCamera(selected.frame, latest) ? 'latest' : 'progress';
    const prediction = selected.phase === 'iterate' ? iterationPlan(selected, budgetMs) : { estimateMs: phaseEstimate(selected.phase, selected) };
    return { kind: actualKind, phase: prediction.finish ? 'iterate-color' : selected.phase, estimateMs: prediction.estimateMs };
  };

  let instanceScratch = new Float32Array(64 * 8);
  const drawInstances = (image, imageWidth, imageHeight, values) => {
    if (!values.length) return;
    const floatCount = values.length * 8;
    if (instanceScratch.length < floatCount) {
      instanceScratch = new Float32Array(Math.max(floatCount, instanceScratch.length * 2));
    }
    for (let i = 0; i < values.length; i++) {
      const item = values[i];
      const base = i * 8;
      for (let j = 0; j < 8; j++) instanceScratch[base + j] = item[j];
    }
    bindQuad(composeProgram); bindTexture(image, 0, composeProgram.uniforms.u_image);
    gl.uniform2f(composeProgram.uniforms.u_textureSize, imageWidth, imageHeight);
    gl.bindBuffer(gl.ARRAY_BUFFER, instancesBuffer);
    gl.bufferData(gl.ARRAY_BUFFER, instanceScratch.subarray(0, floatCount), gl.DYNAMIC_DRAW);
    for (const [attribute, offset] of [[composeProgram.attributes.a_bounds, 0], [composeProgram.attributes.a_uvBounds, 16]]) {
      gl.enableVertexAttribArray(attribute); gl.vertexAttribPointer(attribute, 4, gl.FLOAT, false, 32, offset); gl.vertexAttribDivisor(attribute, 1);
    }
    gl.drawArraysInstanced(gl.TRIANGLES, 0, 6, values.length);
    gl.vertexAttribDivisor(composeProgram.attributes.a_bounds, 0); gl.vertexAttribDivisor(composeProgram.attributes.a_uvBounds, 0);
    gl.disableVertexAttribArray(composeProgram.attributes.a_bounds); gl.disableVertexAttribArray(composeProgram.attributes.a_uvBounds);
  };
  const pollComposition = frameIntervalMs => {
    if (!compositionTimer || !gl.getQueryParameter(compositionTimer.query, gl.QUERY_RESULT_AVAILABLE)) return;
    const disjoint = gl.getParameter(timerExtension.GPU_DISJOINT_EXT);
    const gpuMs = disjoint ? null : gl.getQueryParameter(compositionTimer.query, gl.QUERY_RESULT) / 1e6;
    const provenance = compositionTimer;
    gl.deleteQuery(compositionTimer.query); resources.queries.splice(resources.queries.indexOf(compositionTimer.query), 1); compositionTimer = null;
    if (Number.isFinite(gpuMs) && gpuMs >= 0) {
      stats.compositionGpuMs = gpuMs; stats.compositionEstimateMs = Math.max(0.1, gpuMs * 2.5 + 0.08);
      // Keep the raw delayed timing intact, including driver/idle outliers.
      // An observation begun several display intervals ago is not a current
      // composition forecast; preserve the last eligible estimate instead.
      if (now() - provenance.queryStartedAt <= 2 * frameIntervalMs)
        stats.compositionRecentEstimateMs = stats.compositionEstimateMs;
      stats.compositionTimingSource = 'gpu-timer'; stats.compositionTimingVersion++;
      stats.lastCompositionTiming = { gpuMs, source: 'gpu-timer', retiredAt: now(),
        queryStartedAt: provenance.queryStartedAt, submittedAt: provenance.submittedAt,
        submissionId: provenance.submissionId,
        cameraKey: provenance.cameraKey, cameraRequestedAt: provenance.cameraRequestedAt,
        xRange: provenance.xRange, yRange: provenance.yRange, width, height };
    } else {
      stats.compositionGpuMs = null; stats.compositionTimingSource = 'disjoint-estimate';
    }
  };
  const present = (xRange = latest?.xRange, yRange = latest?.yRange, presentOptions = {}) => {
    if (yRange && !Array.isArray(yRange)) { presentOptions = yRange; yRange = xRange?.y || xRange?.yRange; xRange = xRange?.x || xRange?.xRange; }
    if (presentOptions.sourceOnly) {
      const source = canvas._fractalZoomCache?.frame;
      if (source) { xRange = source.xRange; yRange = source.yRange; }
    }
    if (gl.isContextLost?.()) { failed = true; return false; }
    if (destroyed || disposed && !retainAcceptedOnDisposal || failed || !latest || !(retained || ready()) || !xRange || !yRange) return false;
    if (!presentOptions.sourceOnly && !inFlight && directCompleted && directAssembly &&
        directCompleted.paletteKey === latest.paletteKey && directCompleted.deepZoom === latest.deepZoom) {
      if (commitWebGLProgressiveFrame(canvas, directAssembly)) lastExactKey = directCompleted.key;
      directAssembly = null;
    }
    const exactCache = canvas._fractalZoomCache;
    if (!presentOptions.sourceOnly && !inFlight && lastExactKey !== latest.key &&
        (matchesFrame(exactCache?.frame, latest) || matchesFrame(exactCache?.previousFrame, latest))) {
      if (!matchesFrame(exactCache.frame, latest)) {
        [exactCache.sourceTexture, exactCache.detailTexture] = [exactCache.detailTexture, exactCache.sourceTexture];
        [exactCache.frame, exactCache.previousFrame] = [exactCache.previousFrame, exactCache.frame];
      }
      for (const value of continuations) if (value) value.abandoned = true;
      continuations.fill(null); assembly = null;
      directCompleted = latest; lastExactKey = latest.key;
      exactCache.transition = null; exactCache.interactive = false; exactCache.panPreview = false;
      stats.replayHits = (stats.replayHits || 0) + 1;
      presentOptions = { ...presentOptions, sourceOnly: true };
    }
    if (!inFlight && lastExactKey === latest.key && matchesFrame(exactCache?.frame, latest)) {
      presentOptions = { ...presentOptions, sourceOnly: true };
    }
    // Only query already retired GPU work. Keep one outstanding timer so a
    // slow device cannot accumulate timing resources across display frames.
    pollComposition(presentOptions.frameIntervalMs || 1000 / 60);
    const timingQuery = timerExtension && !compositionTimer ? gl.createQuery() : null;
    const queryStartedAt = timingQuery ? now() : null;
    if (timingQuery) { resources.queries.push(timingQuery); gl.beginQuery(timerExtension.TIME_ELAPSED_EXT, timingQuery); }
    gl.bindFramebuffer(gl.FRAMEBUFFER, null); gl.viewport(0, 0, width, height);
    gl.disable(gl.SCISSOR_TEST); gl.disable(gl.DEPTH_TEST); gl.disable(gl.BLEND);
    gl.clearColor(...BACKGROUND); gl.clear(gl.COLOR_BUFFER_BIT);
    const candidateSource = canvas._fractalZoomCache?.frame;
    const source = candidateSource && candidateSource.paletteKey === latest.paletteKey &&
      candidateSource.width === width && candidateSource.height === height ? candidateSource : null;
    const accepted = presentOptions.sourceOnly ? [] : [...cache.values()].filter(value =>
      value.frame.paletteKey === latest.paletteKey && visible(progressiveTileBounds(value.frame, value.tile, xRange, yRange)));
    // Below one output pixel per original sample, extra projected sampling
    // cannot improve the native display grid. Prefer fresh exact camera data
    // over those old oversampled regions, especially when reversing zoom.
    const latestSpacing = sampleSpacing(latest);
    const effectiveSpacing = frame => Math.max(sampleSpacing(frame), latestSpacing);
    accepted.sort((a, b) => effectiveSpacing(b.frame) - effectiveSpacing(a.frame) ||
      Number(sameCamera(a.frame, latest)) - Number(sameCamera(b.frame, latest)) ||
      a.frame.cameraRequestedAt - b.frame.cameraRequestedAt || a.sequence - b.sequence);
    const atlasInstance = value => {
      const x = value.slot % plan.atlasColumns * plan.tileSize, y = Math.floor(value.slot / plan.atlasColumns) * plan.tileSize;
      return [...progressiveTileBounds(value.frame, value.tile, xRange, yRange), x / plan.atlasWidth, y / plan.atlasHeight,
        (x + value.tile.width) / plan.atlasWidth, (y + value.tile.height) / plan.atlasHeight];
    };
    // Coarser original tiles can fill newly exposed regions, while the finer
    // retained source covers their overlap. Finer accepted tiles are on top.
    const sourceSpacing = source ? effectiveSpacing(source) : 0;
    const coarse = source ? accepted.filter(value => effectiveSpacing(value.frame) > sourceSpacing) : [];
    drawInstances(atlas, plan.atlasWidth, plan.atlasHeight, coarse.map(atlasInstance));
    if (source) {
      drawInstances(canvas._fractalZoomCache.sourceTexture, width, height,
        [[...progressiveTileBounds(source, { x: 0, y: 0, width, height }, xRange, yRange), 0, 0, 1, 1]]);
    }
    const finer = source ? accepted.filter(value => effectiveSpacing(value.frame) <= sourceSpacing) : accepted;
    drawInstances(atlas, plan.atlasWidth, plan.atlasHeight, finer.map(atlasInstance));
    if (timingQuery) {
      gl.endQuery(timerExtension.TIME_ELAPSED_EXT);
      compositionTimer = { query: timingQuery, queryStartedAt, submittedAt: now(),
        submissionId: ++compositionSequence,
        cameraKey: identity(xRange, yRange, width, height, latest.paletteKey,
          presentOptions.sourceOnly && source ? source.deepZoom : latest.deepZoom),
        cameraRequestedAt: presentOptions.sourceOnly && source ? source.cameraRequestedAt : latest.cameraRequestedAt,
        xRange: [...xRange], yRange: [...yRange] };
    }
    canvas._fractalProgressiveTiles = accepted.map(value => ({
      xRange: [...value.frame.xRange], yRange: [...value.frame.yRange], width, height,
      tile: { ...value.tile }, bounds: progressiveTileBounds(value.frame, value.tile, xRange, yRange),
      cameraRequestedAt: value.frame.cameraRequestedAt, paletteKey: value.frame.paletteKey,
      deepZoom: value.frame.deepZoom, exactCurrentCamera: sameCamera(value.frame, latest),
      retained: !!value.retained,
      atlasRect: [value.slot % plan.atlasColumns * plan.tileSize,
        Math.floor(value.slot / plan.atlasColumns) * plan.tileSize, value.tile.width, value.tile.height],
      sampleFootprint: Math.max((value.frame.xRange[1] - value.frame.xRange[0]) / (xRange[1] - xRange[0]),
        (value.frame.yRange[1] - value.frame.yRange[0]) / (yRange[1] - yRange[0])),
    }));
    canvas._fractalProgressiveAtlas = { texture: atlas, width: plan.atlasWidth, height: plan.atlasHeight, tileSize: plan.tileSize };
    const exactTiles = [...cache.values()].filter(value => !value.retained && sameCamera(value.frame, latest) && value.tile.canonical !== false);
    const directExact = (lastExactKey === latest.key || sameCamera(directCompleted, latest)) &&
      matchesFrame(canvas._fractalZoomCache?.frame, latest);
    const complete = exactTiles.length === plan.columns * plan.rows || directExact;
    stats.coverage = directExact ? 1 : exactTiles.reduce((sum, value) => sum + value.tile.width * value.tile.height, 0) / (width * height);
    stats.cameraAgeMs = complete ? 0 : accepted.length ? Math.max(...accepted.map(value =>
      sameCamera(value.frame, latest) ? 0 : now() - value.frame.cameraRequestedAt)) : source ? now() - (source.cameraRequestedAt ?? now()) : Infinity;
    const currentPresentation = xRange[0] === latest.xRange[0] && xRange[1] === latest.xRange[1] &&
      yRange[0] === latest.yRange[0] && yRange[1] === latest.yRange[1];
    if (directExact && currentPresentation && !inFlight) lastExactKey = latest.key;
    if (assembly?.frame.key === latest.key && assembly.copied.size === plan.columns * plan.rows &&
        complete && currentPresentation && !inFlight && lastExactKey !== latest.key) {
      if (commitWebGLProgressiveFrame(canvas, assembly.target)) { lastExactKey = latest.key; directCompleted = null; }
    }
    if (presentOptions.sourceOnly && currentPresentation && !inFlight && matchesFrame(source, latest)) {
      // These private regions are dormant behind the matching uniform exact
      // source that was just drawn. Keeping them would resurrect a previous
      // gesture's hidden coverage on the next camera, obscuring fresh detail.
      // Active/mixed compositions and source-only suspension at a different
      // camera retain their visible regions and original provenance.
      for (const value of [...cache.values()]) removeTile(value);
    }
    if (canvas._fractalZoomCache) {
      canvas._fractalZoomCache.interactive = presentOptions.sourceOnly ? false : !complete || !currentPresentation;
      canvas._fractalZoomCache.panPreview = false;
    }
    recordContinuations();
    canvas._fractalRenderStats = { ...stats, progressive: true, shadedPixels: 0, totalPixels: width * height,
      reprojected: !complete, interactiveZoom: interactive, exact: complete && currentPresentation };
    if (disposed && retainAcceptedOnDisposal) {
      // A presentation during retirement uses the retained compositor and
      // atlas. Move the final fence past that use before deleting or handing
      // off any of their resources.
      if (retirementFence) gl.deleteSync(retirementFence);
      retirementFence = gl.fenceSync(gl.SYNC_GPU_COMMANDS_COMPLETE, 0); gl.flush();
    }
    return { complete: complete && currentPresentation, exactFrame: lastExactKey === latest.key &&
      matchesFrame(canvas._fractalZoomCache?.frame, latest) ? canvas._fractalZoomCache.frame : null };
  };
  const reserveSlot = () => {
    if (!freeSlots.length) {
      const entries = [...cache.values()];
      entries.sort((a, b) => Number(sameCamera(a.frame, latest)) - Number(sameCamera(b.frame, latest)) ||
        Number(visible(progressiveTileBounds(a.frame, a.tile, latest.xRange, latest.yRange))) -
        Number(visible(progressiveTileBounds(b.frame, b.tile, latest.xRange, latest.yRange))) || a.sequence - b.sequence);
      if (entries.length) removeTile(entries[0]);
    }
    return freeSlots.pop();
  };
  const poll = () => {
    if (!inFlight) return { pending: false, inFlight: false, retired: false };
    const submission = inFlight, value = submission.value;
    const status = gl.clientWaitSync(submission.fence, 0, 0);
    if (status === gl.TIMEOUT_EXPIRED && !gl.isContextLost?.()) return { pending: true, inFlight: true, retired: false };
    inFlight = null; gl.deleteSync(submission.fence);
    if (status === gl.WAIT_FAILED || gl.isContextLost?.()) { failed = true; return { pending: false, failed: true, retired: false }; }
    let gpuMs = null;
    if (submission.timer && gl.getQueryParameter(submission.timer, gl.QUERY_RESULT_AVAILABLE) &&
        !gl.getParameter(timerExtension.GPU_DISJOINT_EXT)) {
      const measured = gl.getQueryParameter(submission.timer, gl.QUERY_RESULT) / 1e6;
      if (Number.isFinite(measured) && measured >= 0) gpuMs = measured;
    }
    if (submission.timer) { gl.deleteQuery(submission.timer); resources.queries.splice(resources.queries.indexOf(submission.timer), 1); }
    const fenceMs = Math.max(0, now() - submission.start);
    const cost = gpuMs ?? fenceMs;
    const sameArithmetic = value.frame.deepZoom === latest?.deepZoom;
    if (sameArithmetic && (submission.phase === 'iterate' || submission.phase === 'iterate-color') && gpuMs !== null && submission.steps > 0) {
      // A transient packet cost must not be multiplied into every future
      // kernel for dozens of retirements. Keep the worst of four actual GPU
      // recurrence observations, including fused color conservatively. Only
      // valid samples advance this recovery window; fences never age it out.
      const startup = submission.countBefore === 0;
      const costs = startup ? startupCosts : recurrenceCosts;
      const history = geometryCost(costs, value) || [];
      const rate = gpuMs / submission.steps;
      const sample = { quantum: submission.plannedBatchSteps, rate };
      history.push(startup ? rate : sample);
      if (history.length > RECURRENCE_COST_WINDOW) history.shift();
      rememberCost(costs, value, history);
      // The immutable submission value owns this calibration. Never attach a
      // delayed result to whichever continuation later occupies its slot.
      if (!disposed && !value.abandoned && value.generation === generation && useful(value)) {
        if (startup) value.startupRate = rate;
        else {
          value.recurrenceRates.push(sample);
          if (value.recurrenceRates.length > RECURRENCE_COST_WINDOW) value.recurrenceRates.shift();
        }
      }
    }
    if (sameArithmetic && value.frame.paletteKey === latest?.paletteKey && gpuMs !== null &&
        (submission.phase === 'color' || submission.phase === 'copy')) {
      const costs = submission.phase === 'copy' ? copyCosts : colorCosts;
      const prior = geometryCost(costs, value);
      rememberCost(costs, value, prior === undefined ? gpuMs : Math.max(gpuMs, prior * 0.9));
    }
    stats.gpuMs = gpuMs; stats.timingSource = gpuMs === null ? 'fence-estimate' : 'gpu-timer';
    if (cost > submission.budgetMs) stats.overshoots++;
    if (sameArithmetic && (submission.phase === 'iterate' || submission.phase === 'iterate-color' || submission.phase === 'calibrate')) {
      if (cost > submission.budgetMs) { batchSteps = Math.max(1, Math.floor(batchSteps / 2)); stableSlices = 0; }
      else if (submission.phase === 'iterate' && ++stableSlices >= 8 && cost < submission.budgetMs / 2) {
        batchSteps = Math.min(MAX_BATCH, batchSteps * 2); stableSlices = 0;
      }
    }
    if (gpuMs !== null && (submission.phase === 'color' || submission.phase === 'iterate' &&
        submission.steps / (submission.kernels || 1) <= 1)) {
      minimalOverruns = cost > submission.budgetMs ? minimalOverruns + 1 : 0;
      if (minimalOverruns >= 2) {
        stats.requiresSmallerTile = true; stats.suggestedTileSize = Math.max(16, plan.tileSize / 2);
        stats.tileReductionReason = submission.phase === 'color' ? 'tile-install-overrun' : 'minimum-iteration-overrun';
      }
    }
    stats.batchSteps = batchSteps;
    let accepted = false;
    if (!disposed && !value.abandoned && value.generation === generation) {
      if (submission.phase === 'calibrate') {
        if (calibration && submission.calibrationIndex === calibration.index) {
          if (gpuMs !== null) calibration.samples.push({ pixels: value.tile.width * value.tile.height, gpuMs: gpuMs * MAX_BATCH / submission.steps });
          calibration.index++;
          if (calibration.index >= 8) {
            const large = calibration.samples.filter(sample => sample.pixels > 1).map(sample => sample.gpuMs);
            const small = calibration.samples.filter(sample => sample.pixels === 1).map(sample => sample.gpuMs);
            // Subtract only the minimum measured fixed cost. Add a noise
            // margin before extrapolating a worst-case saturated pixel cost.
            if (large.length >= 3 && small.length >= 3) {
              const upper = Math.max(...large), lower = Math.min(...small);
              const perPixel = Math.max(0, upper - lower) + Math.max(0.0005, upper * 0.1);
              calibration.fullGridEstimateMs = (perPixel / (CALIBRATION_SIZE ** 2 - 1) * width * height *
                (MAX_ITERATIONS / MAX_BATCH) + upper + 0.35) * 1.5;
              stats.fullGridEstimateMs = calibration.fullGridEstimateMs;
            }
          }
        }
      } else if (submission.phase === 'direct') {
        directCompleted = value.frame; directAssembly = submission.directTarget; accepted = true;
        if (cost > submission.budgetMs) {
          if (calibration) calibration.directDisabled = true;
          if (submission.fixtureKey) rejectedDirectFixtures.add(submission.fixtureKey);
          stats.directOverruns = (stats.directOverruns || 0) + 1;
        }
      } else if (submission.phase === 'iterate') {
        value.ping = submission.finalPing; value.initialized = true; value.count += submission.steps;
        if (gl.getQueryParameter(submission.activeQuery, gl.QUERY_RESULT_AVAILABLE) &&
            !gl.getQueryParameter(submission.activeQuery, gl.QUERY_RESULT) || value.count >= MAX_ITERATIONS) value.phase = 'color';
      } else if (submission.phase === 'copy') {
        if (assembly?.frame.key === value.frame.key) assembly.copied.add(value.id);
      } else {
        if (submission.phase === 'iterate-color') {
          value.ping = submission.finalPing; value.initialized = true; value.count += submission.steps; value.phase = 'color';
        }
        cache.set(value.id, { ...value, slot: submission.slot, sequence: ++sequence });
        if (submission.copiedKey && assembly?.frame.key === submission.copiedKey) assembly.copied.add(value.id);
        stats.acceptedTiles++; continuations[value.index] = null; accepted = true;
      }
    } else if ((submission.phase === 'color' || submission.phase === 'iterate-color') && submission.slot !== undefined) freeSlots.push(submission.slot);
    if (submission.activeQuery) { gl.deleteQuery(submission.activeQuery); resources.queries.splice(resources.queries.indexOf(submission.activeQuery), 1); }
    const timing = { gpuMs, fenceMs, kind: submission.kind, phase: submission.phase,
      plannedBatchSteps: submission.plannedBatchSteps,
      estimateMs: submission.estimateMs, submissionBudgetMs: submission.budgetMs,
      steps: submission.steps, pixels: value.tile.width * value.tile.height, tileId: value.id,
      kernels: submission.kernels, cameraRequestedAt: value.frame.cameraRequestedAt,
      canonical: value.tile.canonical !== false, countBefore: submission.countBefore, countAfter: value.count,
      queryStartedAt: submission.queryStartedAt, submittedAt: submission.start, retiredAt: now(), accepted };
    options.onTiming?.(timing);
    stats.lastTiming = timing;
    stats.nextKind = continuations.some(candidate => useful(candidate) && !sameCamera(candidate.frame, latest)) ? nextKind : 'latest';
    stats.estimatedSliceMs = nextSlicePlan({ kind: stats.nextKind, budgetMs: submission.budgetMs }).estimateMs;
    recordContinuations();
    return { pending: false, inFlight: false, retired: true, accepted,
      requiresPresentation: accepted && submission.phase === 'direct', timing, failed, stats };
  };
  const step = (stepOptions = {}) => {
    const retirement = poll();
    if (retirement.pending || disposed || failed || !latest) return retirement;
    if (directAssembly) return { ...retirement, requiresPresentation: true, stats };
    if (canvas.width !== width || canvas.height !== height || gl.isContextLost?.()) return { ...retirement, failed: true };
    if (!ready()) return { ...retirement, pending: false, preparing: !failed, failed };
    const exactCache = canvas._fractalZoomCache;
    if (matchesFrame(exactCache?.frame, latest) || matchesFrame(exactCache?.previousFrame, latest)) {
      return { ...retirement, complete: true, requiresPresentation: lastExactKey !== latest.key, stats,
        exactFrame: lastExactKey === latest.key ? exactCache.frame : null };
    }
    if (stats.requiresSmallerTile || stepOptions.allowSubmit === false || stepOptions.allowSubmission === false || stepOptions.budgetMs === 0) return { ...retirement, stats, exactFrame: lastExactKey === latest.key ? canvas._fractalZoomCache?.frame : null }; 
    let chosen;
    const progressDue = stepOptions.kind === 'progress' && continuations.some(value => useful(value) && !sameCamera(value.frame, latest));
    const exactTiles = [...cache.values()].filter(value => !value.retained && sameCamera(value.frame, latest) && value.tile.canonical !== false);
    // Copy the already accepted tiles in small, separately fenced units into
    // optional history storage, instead of copying a full framebuffer at once.
    if (!progressDue && exactTiles.length === plan.columns * plan.rows && lastExactKey !== latest.key) {
      if (!assembly) {
        const exact = { ...latest, exact: true }; delete exact.colorParams; delete exact.anchor;
        const target = prepareWebGLProgressiveFrame(canvas, exact);
        if (target) assembly = { frame: latest, target, copied: new Set() };
      }
      const tile = assembly && exactTiles.find(value => !assembly.copied.has(value.id));
      if (tile) chosen = { value: { ...tile, phase: 'copy' }, kind: 'latest' };
    }
    const targetBudgetMs = stepOptions.budgetMs ?? Math.min(4, (stepOptions.frameIntervalMs || 16.67) / 4);
    const qualified = options.directAdmission?.(latest, targetBudgetMs);
    const qualifiedKey = qualified?.fixtureKey;
    const qualifiedEstimate = qualified?.estimateMs;
    if (!chosen && !progressDue && options.enableDirect !== false && timerExtension && qualifiedKey &&
        !rejectedDirectFixtures.has(qualifiedKey) && Number.isFinite(qualifiedEstimate) && qualifiedEstimate > 0 &&
        qualifiedEstimate <= targetBudgetMs) {
      if (sameCamera(directCompleted, latest) && matchesFrame(canvas._fractalZoomCache?.frame, latest)) return { ...retirement, complete: true, requiresPresentation: lastExactKey !== latest.key, stats,
        exactFrame: lastExactKey === latest.key ? canvas._fractalZoomCache?.frame : null };
      for (const value of continuations) if (value) value.abandoned = true;
      chosen = { value: { frame: latest, tile: { x: 0, y: 0, width, height }, phase: 'direct',
        index: 0, ping: 0, generation }, kind: 'latest', fixtureKey: qualifiedKey };
      stats.directAdmission = qualifiedKey; stats.directEstimateMs = qualifiedEstimate;
    }
    const directEnabled = options.enableDirect === true || (options.enableDirect !== false && !latest?.deepZoom);
    if (!chosen && directEnabled && timerExtension) {
      if (!calibration) calibration = { index: 0, samples: [], fullGridEstimateMs: null, directDisabled: false };
      if (calibration.index < 8 && !continuations.some(Boolean)) {
        const size = calibration.index % 2 === 0 ? 1 : CALIBRATION_SIZE;
        const fixture = { ...latest, xRange: [-1e-7, 1e-7], yRange: [-1e-7, 1e-7] };
        chosen = { value: { frame: fixture, tile: { x: 0, y: 0, width: size, height: size },
          phase: 'calibrate', index: 0, ping: 0, initialized: false, count: 0, generation }, kind: 'latest' };
      } else if (!progressDue && calibration.index >= 8 && !calibration.directDisabled && calibration.fullGridEstimateMs !== null &&
          calibration.fullGridEstimateMs <= (stepOptions.budgetMs ?? 4)) {
        if (sameCamera(directCompleted, latest) && matchesFrame(canvas._fractalZoomCache?.frame, latest)) return { ...retirement, complete: true, requiresPresentation: lastExactKey !== latest.key, stats, exactFrame: lastExactKey === latest.key ? canvas._fractalZoomCache?.frame : null };
        for (const value of continuations) if (value) value.abandoned = true;
        chosen = { value: { frame: latest, tile: { x: 0, y: 0, width, height }, phase: 'direct',
          index: 0, ping: 0, generation }, kind: 'latest' };
      }
    }
    if (!chosen) chosen = choose(stepOptions.kind); if (!chosen) return { ...retirement, complete: true, stats, exactFrame: lastExactKey === latest.key ? canvas._fractalZoomCache?.frame : null };
    const { value, kind } = chosen;
    const budgetMs = stepOptions.budgetMs ?? Math.min(4, (stepOptions.frameIntervalMs || 16.67) / 4);
    const read = statePool[value.index][value.ping], write = statePool[value.index][1 - value.ping];
    let slot, activeQuery, steps = 0, kernels = 1, finalPing = value.ping, copiedKey = null, directTarget = null, submittedPhase = value.phase, plannedBatchSteps;
    let estimateMs = value.phase === 'direct' ? (qualifiedEstimate ?? calibration?.fullGridEstimateMs ?? budgetMs) :
      value.phase === 'calibrate' ? 0.5 : phaseEstimate(value.phase, value);
    const colorTile = meta => {
      slot = reserveSlot();
      if (slot === undefined) return false;
      const x = slot % plan.atlasColumns * plan.tileSize, y = Math.floor(slot / plan.atlasColumns) * plan.tileSize;
      gl.disable(gl.SCISSOR_TEST); gl.bindFramebuffer(gl.FRAMEBUFFER, atlasFramebuffer); gl.drawBuffers([gl.COLOR_ATTACHMENT0]);
      gl.viewport(x, y, value.tile.width, value.tile.height); bindQuad(colorProgram);
      const u = colorProgram.uniforms, cp = value.frame.colorParams;
      bindTexture(meta, 0, u.u_meta); bindTexture(palette, 1, u.u_palette);
      gl.uniform2i(u.u_origin, x, y); gl.uniform1f(u.u_gScale, cp.gScale ?? 7); gl.uniform1f(u.u_phaseOffset, cp.phaseOffset ?? 0);
      gl.uniform1f(u.u_aScale, cp.aScale ?? 15); gl.uniform1f(u.u_aFixed, cp.aFixed ?? 0);
      gl.uniform1i(u.u_lagoon, cp.style === 'lagoon' ? 1 : 0); gl.uniform1i(u.u_repeat, cp.repeat ? 1 : 0);
      gl.uniform1i(u.u_useMu, cp.useMu === false ? 0 : 1); gl.uniform1i(u.u_useFixedAlpha, cp.aFixed != null ? 1 : 0);
      gl.drawArrays(gl.TRIANGLES, 0, 6);
      if (sameCamera(value.frame, latest) && value.tile.canonical !== false) {
        if (!assembly) {
          const exact = { ...latest, exact: true }; delete exact.colorParams; delete exact.anchor;
          const target = prepareWebGLProgressiveFrame(canvas, exact);
          if (target) assembly = { frame: latest, target, copied: new Set() };
        }
        if (assembly?.frame.key === value.frame.key) {
          gl.bindFramebuffer(gl.FRAMEBUFFER, atlasFramebuffer);
          if (copyWebGLProgressiveTile(canvas, assembly.target, x, y, value.tile.x, value.tile.y,
              value.tile.width, value.tile.height)) copiedKey = value.frame.key;
        }
      }
      return true;
    };
    const timer = timerExtension ? gl.createQuery() : null;
    const queryStartedAt = timer ? now() : null;
    if (timer) { resources.queries.push(timer); gl.beginQuery(timerExtension.TIME_ELAPSED_EXT, timer); }
    gl.disable(gl.BLEND); gl.disable(gl.SCISSOR_TEST); gl.disable(gl.DEPTH_TEST);
    if (value.phase === 'direct') {
      const exact = { ...value.frame, exact: true }; delete exact.colorParams; delete exact.anchor;
      directTarget = prepareWebGLProgressiveFrame(canvas, exact);
      if (directTarget) {
        if (!directFramebuffer) { directFramebuffer = gl.createFramebuffer(); resources.framebuffers.push(directFramebuffer); }
        gl.bindFramebuffer(gl.FRAMEBUFFER, directFramebuffer);
        gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, directTarget.texture, 0);
        if (gl.checkFramebufferStatus(gl.FRAMEBUFFER) !== gl.FRAMEBUFFER_COMPLETE ||
            !drawMandelbrotFractalWebGL(canvas, value.frame.xRange, value.frame.yRange, value.frame.colorParams,
              { zoomTransition: false, offscreenTarget: directFramebuffer })) failed = true;
      } else failed = true;
    } else if (value.phase === 'copy') {
      const x = value.slot % plan.atlasColumns * plan.tileSize, y = Math.floor(value.slot / plan.atlasColumns) * plan.tileSize;
      gl.bindFramebuffer(gl.FRAMEBUFFER, atlasFramebuffer);
      copyWebGLProgressiveTile(canvas, assembly.target, x, y, value.tile.x, value.tile.y, value.tile.width, value.tile.height);
    } else if (value.phase === 'iterate' || value.phase === 'calibrate') {
      gl.bindFramebuffer(gl.FRAMEBUFFER, write.framebuffer); gl.drawBuffers([gl.COLOR_ATTACHMENT0, gl.COLOR_ATTACHMENT1]);
      // Preserve the direct shader's full-viewport interpolation. Translating
      // that viewport into the tile framebuffer keeps identical pixel-center
      // coordinates; rebuilding UVs by division perturbs boundary orbits.
      if (value.phase === 'calibrate') gl.viewport(0, 0, value.tile.width, value.tile.height);
      else gl.viewport(-value.tile.x, -value.tile.y, width, height);
      gl.enable(gl.SCISSOR_TEST); gl.scissor(0, 0, value.tile.width, value.tile.height);
      const iterationProgram = iterationPrograms[value.frame.deepZoom ? 1 : 0]; bindQuad(iterationProgram);
      const u = iterationProgram.uniforms, frame = value.frame;
      gl.uniform2f(u.u_xRange, ...frame.xRange); gl.uniform2f(u.u_yRange, ...frame.yRange);
      const spanX = frame.xRange[1] - frame.xRange[0], spanY = frame.yRange[1] - frame.yRange[0];
      gl.uniform2f(u.u_centerX, ...splitFloat(frame.xRange[0] + spanX / 2));
      gl.uniform2f(u.u_centerY, ...splitFloat(frame.yRange[0] + spanY / 2));
      gl.uniform2f(u.u_spanX, ...splitFloat(spanX)); gl.uniform2f(u.u_spanY, ...splitFloat(spanY));
      gl.uniform2f(u.u_resolution, width, height); gl.uniform2f(u.u_origin, value.tile.x, value.tile.y);
      gl.uniform1f(u.u_arithmeticBound, 1e30);
      const prediction = iterationPlan(value, budgetMs);
      if (value.phase === 'iterate') { kernels = prediction.kernels; plannedBatchSteps = prediction.stepsPerKernel; estimateMs = prediction.estimateMs; }
      for (let kernel = 0; kernel < kernels; kernel++) {
        const kernelRead = statePool[value.index][finalPing], kernelWrite = value.phase === 'calibrate' ? calibrationState : statePool[value.index][1 - finalPing];
        gl.bindFramebuffer(gl.FRAMEBUFFER, kernelWrite.framebuffer); gl.drawBuffers([gl.COLOR_ATTACHMENT0, gl.COLOR_ATTACHMENT1]);
        bindTexture(kernelRead.orbit, 0, u.u_orbit); bindTexture(kernelRead.meta, 1, u.u_meta);
        const count = value.phase === 'calibrate' ? Math.min(MAX_BATCH, batchSteps) : Math.min(plannedBatchSteps, MAX_ITERATIONS - value.count - steps);
        gl.uniform1i(u.u_steps, count); gl.uniform1i(u.u_init, !value.initialized && kernel === 0 ? 1 : 0);
        gl.uniform1i(u.u_calibrate, value.phase === 'calibrate' ? 1 : 0); gl.drawArrays(gl.TRIANGLES, 0, 6);
        steps += count; finalPing = 1 - finalPing;
      }
      if (value.phase === 'iterate' && prediction.finish && value.count + steps >= MAX_ITERATIONS &&
          colorTile(statePool[value.index][finalPing].meta)) submittedPhase = 'iterate-color';
      else {
        // A query checks whether any pixel still needs recurrence without a
        // synchronous readback. Use the other framebuffer to avoid feedback.
        gl.viewport(0, 0, value.tile.width, value.tile.height);
        gl.bindFramebuffer(gl.FRAMEBUFFER, statePool[value.index][1 - finalPing].framebuffer); gl.drawBuffers([gl.COLOR_ATTACHMENT0]);
        bindQuad(reductionProgram); bindTexture(statePool[value.index][finalPing].meta, 0, reductionProgram.uniforms.u_meta);
        if (value.phase !== 'calibrate') { activeQuery = gl.createQuery(); resources.queries.push(activeQuery);
          gl.beginQuery(gl.ANY_SAMPLES_PASSED, activeQuery); gl.drawArrays(gl.TRIANGLES, 0, 6); gl.endQuery(gl.ANY_SAMPLES_PASSED); }
      }
    } else {
      if (!colorTile(read.meta)) { if (timer) gl.endQuery(timerExtension.TIME_ELAPSED_EXT); return { ...retirement, limited: true }; }
    }
    if (timer) gl.endQuery(timerExtension.TIME_ELAPSED_EXT);
    const fence = gl.fenceSync(gl.SYNC_GPU_COMMANDS_COMPLETE, 0);
    if (!fence) { failed = true; return { ...retirement, failed: true }; }
    gl.flush(); gl.disable(gl.SCISSOR_TEST); gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    value.lastSubmitted = now(); stats.submitted++; stats[`${kind}Submissions`]++;
    inFlight = { fence, timer, activeQuery, value, kind, slot, steps, phase: submittedPhase, start: now(), budgetMs, calibrationIndex: calibration?.index, kernels, finalPing, copiedKey, directTarget, fixtureKey: chosen.fixtureKey, plannedBatchSteps,
      queryStartedAt, countBefore: value.count, estimateMs };
    return { ...retirement, pending: true, inFlight: true, submitted: true, kind, phase: submittedPhase, steps, stats,
      estimateMs, submissionBudgetMs: budgetMs, pixels: value.tile.width * value.tile.height };
  };
  return { request, present, step, poll, stats, plan, nextSlicePlan,
    estimateNextSlice(stepOptions) { return nextSlicePlan(stepOptions).estimateMs; },
    get nextSliceEstimateMs() {
      return nextSlicePlan().estimateMs;
    },
    get inFlight() { return !!inFlight; }, get progressPending() { return continuations.some(value => useful(value) && !sameCamera(value.frame, latest)); }, get failed() { return failed; }, get ready() { return ready(); },
    get exactFrame() { return !failed && !gl.isContextLost?.() && latest && lastExactKey === latest.key &&
      matchesFrame(canvas._fractalZoomCache?.frame, latest) ? canvas._fractalZoomCache.frame : null; },
    dispose(disposeOptions = {}) {
      if (disposePromise) return disposePromise;
      disposePromise = new Promise(resolve => { resolveDisposal = resolve; });
      retainAcceptedOnDisposal = !!disposeOptions.retainAccepted;
      disposed = true; for (const value of continuations) if (value) value.abandoned = true;
      // Compute's fence can precede later compositions that still sample the
      // atlas. A final fence covers every queued use of this renderer before
      // its byte admission is released or a replacement allocates storage.
      if (gl.isContextLost?.()) { if (inFlight) { gl.deleteSync(inFlight.fence); inFlight = null; } destroy(); }
      else {
        const retire = () => {
          if (destroyed) return;
          poll();
          if (gl.isContextLost?.()) { if (retirementFence) gl.deleteSync(retirementFence); retirementFence = null; destroy(); return; }
          if (!retirementFence) { retirementFence = gl.fenceSync(gl.SYNC_GPU_COMMANDS_COMPLETE, 0); gl.flush(); }
          const status = retirementFence && gl.clientWaitSync(retirementFence, 0, 0);
          if (status === gl.ALREADY_SIGNALED || status === gl.CONDITION_SATISFIED) {
            gl.deleteSync(retirementFence); retirementFence = null;
            if (!inFlight) { destroy(); return; }
          }
          setTimeout(retire, 1);
        };
        retirementFence = gl.fenceSync(gl.SYNC_GPU_COMMANDS_COMPLETE, 0); gl.flush();
        setTimeout(retire, 1);
      }
      return disposePromise;
    },
  };
}
